import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * A → Sattvik
 * B → Rajasik
 * C → Tamasik
 */

const quizData = [
  {
    question: "When I wake up and have a little extra time before starting my day, I usually…",
    options: [
      "Sit quietly, stretch, pray, journal, or just enjoy the calm",
      "Check my phone, plan my day, or start doing something productive",
      "Hit snooze, lie in bed, or scroll without really thinking",
    ],
  },
  {
    question: "When I have to make an important decision, my natural tendency is to…",
    options: [
      "Pause, reflect, and wait until I feel clarity",
      "Feel restless until I take action",
      "Delay it and hope it somehow sorts itself out",
    ],
  },
  {
    question: "After a long or tiring day, I relax best by…",
    options: [
      "Quiet activities like reading, music, or meaningful conversation",
      "Watching shows, social media, talking, or staying busy",
      "Sleeping, lying down, or mentally switching off",
    ],
  },
  {
    question: "When it comes to food, I notice that I usually eat…",
    options: [
      "Mindfully and in moderation",
      "Irregularly — sometimes overeating, sometimes skipping",
      "Mostly out of habit, boredom, or convenience",
    ],
  },
  {
    question: "In group settings or social situations, I tend to…",
    options: [
      "Observe, listen, and speak when I feel it’s meaningful",
      "Take initiative, lead conversations, or stay very involved",
      "Stay quiet, withdrawn, or mentally checked out",
    ],
  },
  {
    question: "When my plans suddenly change, my inner response is usually…",
    options: [
      "“Okay, I’ll adapt”",
      "Feeling irritated, rushed, or frustrated",
      "Feeling low, stuck, or demotivated",
    ],
  },
  {
    question: "My sleep routine on most days looks like…",
    options: [
      "Fairly consistent and refreshing",
      "Irregular because my mind stays active",
      "Excessive, disturbed, or very unstructured",
    ],
  },
  {
    question: "When I get free time with nothing planned, I usually…",
    options: [
      "Use it intentionally or consciously",
      "Fill it with activity or stimulation",
      "Let it pass without much awareness",
    ],
  },
  {
    question: "My mental state through the day generally feels…",
    options: [
      "Clear, calm, and steady",
      "Busy, fast, and restless",
      "Heavy, dull, or foggy",
    ],
  },
  {
    question: "When I start something new (a habit, course, or idea), I usually…",
    options: [
      "Enjoy the process itself",
      "Focus mainly on results and outcomes",
      "Feel resistant, uninterested, or unmotivated",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */
const IntroPage = ({ onStart }) => (
  <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
    <div
      className="
        relative
        overflow-hidden
        rounded-[34px]
        max-w-5xl
        w-full
        h-[620px]
        shadow-[0_30px_80px_rgba(20,10,0,.12)]
      "
    >
      <img
        src="/assets/knowyourself/quiz/guna.jpg"
        alt="Guna Assessment"
        className="
          w-full
          h-full
          object-cover
          brightness-[.84]
          saturate-[.9]
        "
      />

      <div
        className="
          absolute
          inset-0
          bg-gradient-to-t
          from-black/90
          via-black/35
          to-transparent
        "
      />

      <div className="absolute inset-x-0 bottom-0 p-6">
        <div
          className="
            rounded-[28px]
            bg-white/10
            backdrop-blur-2xl
            border
            border-white/10
            p-8
          "
        >
          <div
            className="
              inline-flex
              px-4
              py-2
              rounded-full
              bg-white/10
              backdrop-blur-xl
              border
              border-white/10
              text-white
              text-xs
              tracking-[.08em]
              mb-5
            "
          >
            SELF ASSESSMENT
          </div>

          <h1
            className="
              text-[38px]
              md:text-[56px]
              leading-[1.05]
              text-white
              mb-5
            "
          >
            Guna Self Assessment
          </h1>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-4
            "
          >
            Discover the balance of Sattvik, Rajasik,
            and Tamasik qualities influencing your
            thoughts, emotions, habits, and decisions.
          </p>

          <p
            className="
              text-white/75
              text-[15px]
              leading-8
              mb-8
            "
          >
            There are no right or wrong answers —
            choose what feels most true for you on
            most days.
          </p>

          <Button
            variant="primary"
            text="Start Assessment"
            onClick={onStart}
          />
        </div>
      </div>
    </div>
  </div>
);


/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[currentQuestion] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[currentQuestion] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (currentQuestion < quizData.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (currentQuestion > 0) setCurrentQuestion(currentQuestion - 1);
  };

  const progress = ((currentQuestion + 1) / quizData.length) * 100;
  const current = quizData[currentQuestion];

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {currentQuestion + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {current.question}
        </p>

        <div className="space-y-3">
          {current.options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[currentQuestion] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[currentQuestion] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {currentQuestion > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={currentQuestion === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  let sattvik = 0;
  let rajasik = 0;
  let tamasik = 0;

  answers.forEach((a) => {
    if (a === 0) sattvik++;
    if (a === 1) rajasik++;
    if (a === 2) tamasik++;
  });

  let title = "";
  let resultText = "";

  if (sattvik >= 5) {
    title = "🌿 Predominantly Sattvik";

    resultText =
      "You naturally lean toward clarity, balance, awareness, and calm. Your strengths include inner stability, reflection, wisdom, and conscious living. Continue nurturing awareness while avoiding excessive detachment.";
  } else if (rajasik >= 5) {
    title = "🔥 Predominantly Rajasik";

    resultText =
      "You are driven, ambitious, action-oriented, and highly motivated. Your strengths include leadership, productivity, and momentum. Focus on balancing effort with rest, reflection, and stillness.";
  } else if (tamasik >= 5) {
    title = "🌑 Predominantly Tamasik";

    resultText =
      "You may currently be experiencing fatigue, heaviness, inertia, or low motivation. Small positive actions, movement, routine, sunlight, and mindful habits can gradually shift your energy.";
  } else {
    title = "🌈 Mixed Guna Profile";

    resultText =
      "Your personality reflects a dynamic blend of Sattvik, Rajasik, and Tamasik qualities. This is very common. Gunas naturally shift based on lifestyle, environment, stress levels, food, and life circumstances.";
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-6 py-10">
      <div
        className="
          relative
          overflow-hidden
          rounded-[34px]
          max-w-5xl
          w-full
          h-[620px]
          shadow-[0_30px_80px_rgba(20,10,0,.12)]
        "
      >
        <img
          src="/assets/knowyourself/quiz/result.jpg"
          alt="Assessment Result"
          className="
            w-full
            h-full
            object-cover
            brightness-[.84]
            saturate-[.9]
          "
        />

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-t
            from-black/90
            via-black/35
            to-transparent
          "
        />

        <div className="absolute inset-x-0 bottom-0 p-6">
          <div
            className="
              rounded-[28px]
              bg-white/10
              backdrop-blur-2xl
              border
              border-white/10
              p-8
            "
          >
            <div
              className="
                inline-flex
                px-4
                py-2
                rounded-full
                bg-white/10
                backdrop-blur-xl
                border
                border-white/10
                text-white
                text-xs
                tracking-[.08em]
                mb-5
              "
            >
              YOUR RESULT
            </div>

            <h2
              className="
                text-[34px]
                md:text-[52px]
                leading-[1.05]
                text-white
                mb-4
              "
            >
              {title}
            </h2>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-4
              "
            >
              Sattvik: {sattvik} • Rajasik: {rajasik} • Tamasik: {tamasik}
            </p>

            <p
              className="
                text-white/75
                text-[15px]
                leading-8
                mb-8
                max-w-3xl
              "
            >
              {resultText}
            </p>

            <Button
              variant="primary"
              text="Retake Assessment"
              onClick={onRetake}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */

const GunaSelfAssessmentQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage answers={answers} onRetake={() => {
          setStarted(false);
          setCompleted(false);
          setAnswers(null);
        }} />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default GunaSelfAssessmentQuiz;
